// Hooks für Touren, Stops, GPS-Tracking.

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Order } from "@/lib/orders";

export type TourStatus = "draft" | "planned" | "released" | "active" | "finished";
export type StopStatus = "pending" | "arrived" | "delivered" | "absent" | "skipped";

export interface DeliveryTour {
  id: string;
  tour_date: string;
  driver_id: string | null;
  status: TourStatus;
  planned_polyline: string | null;
  planned_distance_m: number | null;
  planned_duration_s: number | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface DeliveryStop {
  id: string;
  tour_id: string;
  order_id: string;
  seq: number;
  eta_at: string | null;
  arrived_at: string | null;
  completed_at: string | null;
  status: StopStatus;
  skip_reason: string | null;
  photo_url: string | null;
  cash_collected_cents: number | null;
}

export interface StopWithOrder extends DeliveryStop {
  order: (Pick<Order, "id" | "order_number" | "total_cents" | "payment_method" | "notes"> & {
    customer: { display_name: string; phone: string | null } | null;
    address: { street: string; zip: string; city: string; geo_lat: number | null; geo_lng: number | null; delivery_notes: string | null } | null;
    items: Array<{ qty: number; product: { name: string; unit: string } }>;
  }) | null;
}

export interface TourWithStops extends DeliveryTour {
  stops: StopWithOrder[];
}

const STOP_SELECT = `
  *,
  order:orders(
    id, order_number, total_cents, payment_method, notes,
    customer:customers(display_name, phone),
    address:customer_addresses(street, zip, city, geo_lat, geo_lng, delivery_notes),
    items:order_items(qty, product:products(name, unit))
  )
`;

// ---------- Backoffice ----------

export function useTours(date?: string) {
  return useQuery({
    queryKey: ["tours", date ?? "all"],
    queryFn: async () => {
      let q = supabase.from("delivery_tours").select("*").order("tour_date", { ascending: false });
      if (date) q = q.eq("tour_date", date);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as DeliveryTour[];
    },
  });
}

export function useTourWithStops(tourId?: string) {
  return useQuery({
    queryKey: ["tour", tourId],
    enabled: !!tourId,
    queryFn: async () => {
      const { data: tour, error } = await supabase
        .from("delivery_tours").select("*").eq("id", tourId!).single();
      if (error) throw error;
      const { data: stops, error: sErr } = await supabase
        .from("delivery_stops")
        .select(STOP_SELECT)
        .eq("tour_id", tourId!)
        .order("seq");
      if (sErr) throw sErr;
      return { ...(tour as DeliveryTour), stops: (stops ?? []) as StopWithOrder[] };
    },
  });
}

export function useReleaseTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase
        .from("delivery_tours")
        .update({ status: "released" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tours"] });
      qc.invalidateQueries({ queryKey: ["tour", vars.id] });
      qc.invalidateQueries({ queryKey: ["my-tour"] });
    },
  });
}

export function useAssignDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, driverId }: { id: string; driverId: string | null }) => {
      const { error } = await supabase
        .from("delivery_tours")
        .update({ driver_id: driverId })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tour", vars.id] });
      qc.invalidateQueries({ queryKey: ["tours"] });
    },
  });
}

// ---------- Fahrer ----------

export function useMyActiveTour() {
  return useQuery({
    queryKey: ["my-tour"],
    queryFn: async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return null;
      const today = new Date().toISOString().slice(0, 10);
      const { data: tour, error: terr } = await supabase
        .from("delivery_tours")
        .select("*")
        .eq("driver_id", user.id)
        .in("status", ["released", "active"])
        .eq("tour_date", today)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (terr) { console.error("useMyActiveTour tour error:", terr); throw terr; }
      if (!tour) return null;
      const { data: stops, error } = await supabase
        .from("delivery_stops")
        .select(STOP_SELECT)
        .eq("tour_id", tour.id)
        .order("seq");
      if (error) throw error;
      return { ...(tour as DeliveryTour), stops: (stops ?? []) as StopWithOrder[] };
    },
    refetchInterval: 30_000,
  });
}

export function useStartTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tourId: string) => {
      const { error } = await supabase
        .from("delivery_tours")
        .update({ status: "active", started_at: new Date().toISOString() })
        .eq("id", tourId);
      if (error) throw error;
      // Alle Orders der Stops auf in_delivery setzen, damit Kunden Live-Tracking sehen
      const { data: stops } = await supabase
        .from("delivery_stops").select("order_id").eq("tour_id", tourId);
      const orderIds = (stops ?? []).map((s) => s.order_id);
      if (orderIds.length) {
        await supabase.from("orders")
          .update({ status: "in_delivery" })
          .in("id", orderIds)
          .in("status", ["planned", "placed"]);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tour"] });
      qc.invalidateQueries({ queryKey: ["tours"] });
      qc.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });
}

export function useFinishTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tourId: string) => {
      const { error } = await supabase
        .from("delivery_tours")
        .update({ status: "finished", finished_at: new Date().toISOString() })
        .eq("id", tourId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tour"] });
      qc.invalidateQueries({ queryKey: ["tours"] });
    },
  });
}

export function useStopAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stopId, action, photoUrl, cashCollectedCents, skipReason }: {
      stopId: string;
      action: "delivered" | "absent" | "skipped";
      photoUrl?: string;
      cashCollectedCents?: number;
      skipReason?: string;
    }) => {
      const now = new Date().toISOString();
      const update: Partial<DeliveryStop> = { status: action };
      if (action === "delivered") {
        update.arrived_at = now;
        update.completed_at = now;
        if (cashCollectedCents != null) update.cash_collected_cents = cashCollectedCents;
      } else if (action === "absent") {
        update.arrived_at = now;
        update.completed_at = now;
        if (photoUrl) update.photo_url = photoUrl;
      } else if (action === "skipped") {
        update.skip_reason = skipReason ?? null;
      }
      const { data: stop, error } = await supabase
        .from("delivery_stops").update(update).eq("id", stopId).select("*, order:orders(payment_method, total_cents)").single();
      if (error) throw error;

      // Order-Status angleichen
      if (action === "delivered") {
        await supabase.from("orders").update({ status: "delivered" }).eq("id", stop.order_id);
      } else if (action === "absent") {
        await supabase.from("orders").update({ status: "failed" }).eq("id", stop.order_id);
      }

      // Bei Lieferung: Payment + Receipt/Invoice automatisch erzeugen
      if (action === "delivered") {
        const order = Array.isArray(stop.order) ? stop.order[0] : stop.order;
        if (order) {
          const user = (await supabase.auth.getUser()).data.user;
          if (order.payment_method === "cash") {
            // Bar: Quittung
            const amount = cashCollectedCents ?? order.total_cents;
            await supabase.from("payments").insert({
              order_id: stop.order_id,
              method: "cash",
              amount_cents: amount,
              status: "cash_pending_handover",
              collected_by: user?.id ?? null,
              paid_at: now,
            });
            const yr = new Date().getFullYear();
            const { data: seq } = await supabase.rpc("next_receipt_number", { year_in: yr });
            await supabase.from("receipts").insert({
              order_id: stop.order_id,
              receipt_number: `HS-${yr}-${String(seq ?? 0).padStart(5, "0")}`,
            });
          } else {
            // Nicht-Bar: Rechnung erzeugen (Status open, fällig in 14 Tagen für invoice, sonst paid via Online-Pay)
            // Order-Daten + Items laden für Rechnungs-Zeilen
            const { data: o } = await supabase
              .from("orders")
              .select(`
                customer_id, total_cents, payment_method, delivery_date,
                items:order_items(qty, unit_price_cents, product:products(name, vat_rate))
              `)
              .eq("id", stop.order_id)
              .single();
            if (o) {
              const dueDays = o.payment_method === "invoice" ? 14 : 0;
              const dueAt = new Date();
              dueAt.setDate(dueAt.getDate() + dueDays);
              const status = o.payment_method === "invoice" || o.payment_method === "sepa" ? "open" : "paid";
              const subtotal = o.total_cents;
              const vat = Math.round(subtotal - subtotal / 1.07);
              const { data: inv } = await supabase.from("invoices").insert({
                customer_id: o.customer_id,
                period_start: o.delivery_date,
                period_end: o.delivery_date,
                status,
                subtotal_cents: subtotal,
                vat_cents: vat,
                total_cents: subtotal,
                paid_cents: status === "paid" ? subtotal : 0,
                due_at: dueAt.toISOString().slice(0, 10),
                paid_at: status === "paid" ? now : null,
              }).select().single();
              if (inv) {
                const lines = (o.items ?? []).map((i) => {
                  const product = Array.isArray(i.product) ? i.product[0] : i.product;
                  const lineTotal = i.qty * i.unit_price_cents;
                  return {
                    invoice_id: inv.id,
                    order_id: stop.order_id,
                    description: product?.name ?? "—",
                    qty: i.qty,
                    unit_price_cents: i.unit_price_cents,
                    vat_rate: product?.vat_rate ?? 7,
                    line_total_cents: lineTotal,
                  };
                });
                if (lines.length) await supabase.from("invoice_lines").insert(lines);
              }
            }
          }
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tour"] });
      qc.invalidateQueries({ queryKey: ["tour"] });
      qc.invalidateQueries({ queryKey: ["cash-today"] });
    },
  });
}

// ---------- GPS-Tracking ----------

export function useGpsTracking(tourId: string | undefined, active: boolean) {
  const [error, setError] = useState<string | null>(null);
  const [lastPing, setLastPing] = useState<{ ts: number; lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!tourId || !active || !navigator.geolocation) return;
    let cancelled = false;

    const send = async (pos: GeolocationPosition) => {
      try {
        const { data } = await supabase.auth.getSession();
        const res = await fetch("/api/tour?action=ping", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.session?.access_token ?? ""}`,
          },
          body: JSON.stringify({
            tour_id: tourId,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            speed_kmh: pos.coords.speed ? pos.coords.speed * 3.6 : undefined,
            heading: pos.coords.heading ?? undefined,
            accuracy_m: pos.coords.accuracy,
          }),
        });
        if (!res.ok && !cancelled) setError(`Ping ${res.status}`);
        else if (!cancelled) {
          setLastPing({ ts: Date.now(), lat: pos.coords.latitude, lng: pos.coords.longitude });
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    };

    const id = navigator.geolocation.watchPosition(
      send,
      (e) => setError(e.message),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 30_000 }
    );
    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(id);
    };
  }, [tourId, active]);

  return { error, lastPing };
}

// ---------- Live position für Kunden ----------

export function useTourLivePosition(tourId: string | undefined) {
  return useQuery({
    queryKey: ["tour-live", tourId],
    enabled: !!tourId,
    queryFn: async () => {
      const { data } = await supabase
        .from("driver_track_points")
        .select("lat, lng, ts")
        .eq("tour_id", tourId!)
        .order("ts", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as { lat: number; lng: number; ts: string } | null;
    },
    refetchInterval: 15_000,
  });
}

// ---------- Helpers ----------

export const STATUS_TOUR_LABEL: Record<TourStatus, string> = {
  draft: "Entwurf", planned: "Geplant", released: "Freigegeben",
  active: "Aktiv", finished: "Beendet",
};

export const STATUS_STOP_LABEL: Record<StopStatus, string> = {
  pending: "Anstehend", arrived: "Angekommen", delivered: "Geliefert",
  absent: "Abgelegt (nicht da)", skipped: "Übersprungen",
};

export function decodePolyline(encoded: string): Array<{ lat: number; lng: number }> {
  // Standard Google Encoded Polyline Algorithm (level 5).
  const points: Array<{ lat: number; lng: number }> = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b: number, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    shift = 0; result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}
